import React from 'react'
import {
  Row, Col, Card, CardBody,
} from 'reactstrap'
import Spinner from 'components/common/Loader'
import { Line, ChartDataFunction } from 'react-chartjs-2'
import * as chartjs from 'chart.js'
import { ChartOptions } from 'chart.js'
import { rgbaColor, chartColors } from 'components/charts/colors'
import { gql, useQuery } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import round from 'lodash/round'
import { useFilters } from './FiltersContext'

interface ToolTipItem {
  index: number
  yLabel: string
}

function getTrailingDays(from: moment.Moment, days: number) {
  const end = moment(from)
  const begin = end.clone().subtract(days, 'days')
  const dayArry: moment.Moment[] = []
  let currDay = begin
  while (currDay <= end) {
    dayArry.push(currDay)
    currDay = currDay.clone().add(1, 'day')
  }
  return dayArry
}

function getDaysQuery(from: moment.Moment, numDays: number) {
  const days = getTrailingDays(from, numDays)
  const dayQueries = days.map((_, i) => {
    if (i === numDays) {
      return ''
    }
    return `
    a${i}: chart {
      averageTeamComfort(filters: { tags: $tags, startDate: $d${i}, endDate: $d${i + 1} })
    }
    `
  })

  const inputs = days.map((day, i) => `$d${i}: DateTime!`)

  const variables = days.reduce((varMap, day, i) => {
    return { ...varMap, [`d${i}`]: day.toDate() }
  }, {} as { [key: string]: Date })

  const query = gql(`
    query QuestionAnswers($tags: [String!], ${inputs.join(', ')}) {
      ${dayQueries.join('\n')}
    }
  `)

  return {
    query,
    days,
    variables: {
      ...variables,
    },
  }
}

const NumberOfDays = 14

const EmployeeComfortCard: React.FC = (props) => (
  <Card className="mb-3" style={{ backgroundImage: 'linear-gradient(-45deg, #014ba7, #0183d0)' }}>
    <CardBody className="rounded-soft">
      <Row className="text-white align-items-center no-gutters">
        <Col>
          <h4 className="text-white mb-1">Comfort Level</h4>
        </Col>
      </Row>
      {props.children}
    </CardBody>
  </Card>
)

const EmployeeComfortLineChart = () => {
  const [filters] = useFilters()

  const { query, variables, days } = getDaysQuery(
    moment().subtract(1, 'day').startOf('day'),
    NumberOfDays,
  )
  const result = useQuery(query, { variables: { ...variables, tags: filters.tags } })

  if (result.loading) {
    return (
      <EmployeeComfortCard>
        <Spinner color="light" />
      </EmployeeComfortCard>
    )
  }

  if (result.error) {
    console.error('errors: ', result.error)
    return (
      <EmployeeComfortCard>
        <div className="d-flex justify-content-center">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            color={chartColors.medium}
            transform="grow-30"
            className="mt-3"
          />
        </div>
        <div className="mt-4 text-center fs--1">
          <p>There was an error fetching the team comfort level data</p>
        </div>
      </EmployeeComfortCard>
    )
  }

  const dayLabels = days
    .map((d) => d.toDate().toLocaleDateString())
    .slice(1)

  const comfortLevels = Object.keys(result.data)
    .map((key) => parseInt(key.substr(1), 10))
    .sort()
    .map((key) => result.data[`a${key.toString()}`].averageTeamComfort)

  const config: { data: ChartDataFunction<chartjs.ChartData>; options: ChartOptions } = {
    data(canvas: any) {
      const ctx = canvas.getContext('2d')
      const gradientFill = ctx.createLinearGradient(0, 0, 0, 250)
      gradientFill.addColorStop(0, 'rgba(255, 255, 255, 0.3)')
      gradientFill.addColorStop(1, 'rgba(255, 255, 255, 0)')

      return {
        labels: dayLabels,
        datasets: [
          {
            borderWidth: 2,
            data: comfortLevels,
            borderColor: rgbaColor('#fff', 0.8),
            backgroundColor: gradientFill,
          },
        ],
      }
    },
    options: {
      legend: { display: false },
      tooltips: {
        mode: 'x-axis',
        xPadding: 20,
        yPadding: 10,
        displayColors: false,
        callbacks: {
          label: (tooltipItem: ToolTipItem) => (
            `${dayLabels[tooltipItem.index]} - ${round(comfortLevels[tooltipItem.index], 1)} out of 5`
          ),
          title: () => '', // makes title blank rather than showing 'undefined'
        },
      },
      hover: { mode: 'label' },
      scales: {
        xAxes: [
          {
            scaleLabel: {
              labelString: 'Month',
            },
            ticks: {
              fontColor: rgbaColor('#fff', 0.7),
              fontStyle: '600',
              maxTicksLimit: 7,
            },
            gridLines: {
              color: rgbaColor('#fff', 0.1),
              zeroLineColor: rgbaColor('#fff', 0.1),
              lineWidth: 1,
            },
          },
        ],
        yAxes: [
          {
            display: true,
            ticks: {
              min: 0,
              max: 5,
              fontColor: rgbaColor('#fff', 0.7),
              fontStyle: '600',
              maxTicksLimit: 5,
            },
            gridLines: {
              color: rgbaColor('#fff', 0.1),
              zeroLineColor: rgbaColor('#fff', 0.1),
              lineWidth: 1,
            },
          },
        ],
      },
    },
  }

  return (
    <EmployeeComfortCard>
      <Line data={config.data} options={config.options} height={75} />
    </EmployeeComfortCard>
  )
}

export default EmployeeComfortLineChart
